import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, List, ListItem, ListItemText, makeStyles } from '@material-ui/core';
import MessageIcon from '@material-ui/icons/Message';
import CropFreeIcon from '@material-ui/icons/CropFree';
import AuthLayout from '../../../components/layouts/AuthLayout';
import Typography from '../../../components/common/Typography';
import { Button } from '../../../components/common/Buttons';
import { Divider } from '../../../components/common/Divider';
import { SpacingContainer } from '../../../components/common/Containers/Base';
import { AmplifyAuth } from '../../../services/amplifyAuth';
import { useTrans } from '../../../services/i18n';
import useLogout from '../../../hooks/useLogout';
import {
  getPostAuthRedirectPath,
  isAllowMfaSkipEnabled,
  isMfaConfigured,
} from '../../../utils/mfa';
import { markAdminMfaSessionSatisfied } from '../../../utils/adminMfaSession';
import SmsFa from '../../Profile/TwoFactorAuthentication/SmsFa';
import AuthenticatorAppFa from '../../Profile/TwoFactorAuthentication/AuthenticatorAppFa';
import SuccessMfa from '../../Profile/TwoFactorAuthentication/SuccessMfa';
import connect from './connect';

const useStyles = makeStyles((theme) => ({
  content: {
    padding: '2.5rem 2rem',
    color: theme.palette.gray.main,
  },
  header: {
    marginBottom: '1.5rem',
  },
  iconColor: {
    color: theme.palette.primary.main,
  },
  marginLeft: {
    marginLeft: '0.5rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1.5rem',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  skipNote: {
    marginTop: '1rem',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginLeft: 'auto',
  },
}));

function SetupMfa({ authUser = {}, updateMFA, updatePhone }) {
  const { trans } = useTrans();
  const navigate = useNavigate();
  const logout = useLogout();
  const styles = useStyles();
  const [twoFaType, setTwoFaType] = useState(null);
  const [qrCode, setQrCode] = useState(undefined);
  const [success, setSuccess] = useState(false);

  const phone = authUser.phone;

  useEffect(() => {
    if (isMfaConfigured(authUser)) {
      markAdminMfaSessionSatisfied(authUser.username);
      navigate(getPostAuthRedirectPath(authUser), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prepareAuthenticator = useCallback(() => {
    AmplifyAuth.currentAuthenticatedUser()
      .then((user) => {
        const username = user.attributes?.preferred_username || user.username;
        const issuer = 'USFCR Admin';

        return AmplifyAuth.setupTOTP(user).then((secretCode) =>
          setQrCode(`otpauth://totp/${username}?secret=${secretCode}&issuer=${issuer}`)
        );
      })
      .catch(console.warn);
  }, []);

  const onSelectMethod = (method) => {
    setTwoFaType(method);
    setSuccess(false);

    if (method === 'app') {
      prepareAuthenticator();
    }
  };

  const onSetupSuccess = () => {
    setSuccess(true);
  };

  const onContinue = () => {
    markAdminMfaSessionSatisfied(authUser.username);
    navigate('/dashboard', { replace: true });
  };

  const onSkipForNow = () => {
    markAdminMfaSessionSatisfied(authUser.username);
    navigate('/dashboard', { replace: true });
  };

  const allowSkip = isAllowMfaSkipEnabled();

  return (
    <AuthLayout>
      <SpacingContainer className={styles.content}>
        <div className={styles.header}>
          <Typography variant="h2" weight="600" gutter="bottom" color="label">
            {trans('two-factor-authentication')}
          </Typography>
          <Typography variant="body1" color="label">
            {allowSkip ? trans('setup-mfa-description-with-skip') : trans('setup-mfa-description')}
          </Typography>
          {allowSkip && (
            <Typography variant="body2" color="label" className={styles.skipNote}>
              {trans('setup-mfa-skip-note')}
            </Typography>
          )}
        </div>

        {!twoFaType && (
          <List>
            <ListItem disableGutters>
              <MessageIcon color="primary" />
              <ListItemText className={styles.marginLeft}>
                <Typography variant="h6" component="p" weight="600" color="label">
                  {trans('sms')}
                </Typography>
              </ListItemText>
              <Button color="success" variant="outlined" onClick={() => onSelectMethod('sms')}>
                {trans('enable')}
              </Button>
            </ListItem>

            <Divider light spacing={1} />

            <ListItem disableGutters>
              <CropFreeIcon className={styles.iconColor} />
              <ListItemText className={styles.marginLeft}>
                <Typography variant="h6" component="p" weight="600" color="label">
                  {trans('authenticator-app')}
                </Typography>
              </ListItemText>
              <Button color="success" variant="outlined" onClick={() => onSelectMethod('app')}>
                {trans('enable')}
              </Button>
            </ListItem>
          </List>
        )}

        {twoFaType === 'sms' && !success && (
          <SmsFa
            userPhone={phone}
            updateMFA={updateMFA}
            updatePhone={updatePhone}
            setSuccess={onSetupSuccess}
          />
        )}

        {twoFaType === 'app' && !success && (
          <AuthenticatorAppFa
            qrCode={qrCode}
            updateMFA={updateMFA}
            setSuccess={onSetupSuccess}
          />
        )}

        {success && (
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <SuccessMfa type={twoFaType} />
            </Grid>
            <Grid item>
              <Button variant="contained" color="primary" size="large" onClick={onContinue}>
                Continue to dashboard
              </Button>
            </Grid>
          </Grid>
        )}

        <div className={styles.actions}>
          {twoFaType && !success && (
            <Button variant="text" color="primary" onClick={() => setTwoFaType(null)}>
              Choose a different method
            </Button>
          )}
          <div className={styles.actionGroup}>
            {allowSkip && !success && (
              <Button variant="text" color="primary" onClick={onSkipForNow}>
                {trans('skip-for-now')}
              </Button>
            )}
            <Button variant="outlined" color="secondary" onClick={logout}>
              {trans('logout')}
            </Button>
          </div>
        </div>
      </SpacingContainer>
    </AuthLayout>
  );
}

export default connect(SetupMfa);
